import React from 'react';
import { Flex, HelpTip, Text, ThemeProvider } from '@amzn/storm-ui';
import {
  CountrySingleSelector,
  ManageCountryListLink,
} from '@amzn/storm-ui-country-selector';
import { formatLocale } from 'src/providers/PantherLocalizationProvider/mbm';
import { CountrySwitcherPublisher } from 'src/commons/utils/metrics';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import useAccountInfo from 'src/commons/hooks/useAccountInfo';
import { ManageCountryListLinkWrapper } from 'src/features/header/components/MarketplaceSwitcher/styles';
import { useHeaderBundle } from 'src/providers/PantherLocalizationProvider';

const modifyPopoverZIndex = (theme) => {
  return {
    ...theme,
    modal: {
      ...theme.modal,
      zIndex: 1000,
    },
    popover: {
      ...theme.popover,
      portalElementId: 'AACChromePortal',
      zIndex: 500,
    },
  };
};

export interface MarketplaceSwitcherProps {
  pathOverride?: string;
}

const MarketplaceSwitcher: React.VFC<MarketplaceSwitcherProps> = ({
  pathOverride = '',
}) => {
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const {
    countryCodes,
    countryCodeToMarketplaceMap,
    canManageAccounts,
    currentAccount,
    globalAccountId,
    selectedCountryCode,
  } = useAccountInfo();
  const bundle = useHeaderBundle();

  const handleSelect = (selectedCode: string) => {
    const marketplace = countryCodeToMarketplaceMap.get(selectedCode);
    if (marketplace?.url) {
      const redirectUrl = new URL(`https://${marketplace.url}`);

      if (pathOverride !== '') {
        redirectUrl.pathname = pathOverride;
      }

      CountrySwitcherPublisher.publishCounterMonitor('switched', 1);
      window.location.assign(redirectUrl.toString());
    }
  };

  if (
    currentAccount?.entityId === undefined ||
    (Array.isArray(countryCodes) && countryCodes.length === 0)
  ) {
    return null;
  }

  CountrySwitcherPublisher.publishCounterMonitor('rendered', 1);
  return (
    <ThemeProvider theme={modifyPopoverZIndex}>
      <CountrySingleSelector
        id="aac-chrome"
        taktId="header_marketplace_switcher"
        locale={formatLocale(internalChromeConfig.locale)}
        onSelect={handleSelect}
        countries={countryCodes}
        selected={selectedCountryCode}
        renderOptionsEnd={() =>
          canManageAccounts && (
            <ManageCountryListLinkWrapper>
              <ManageCountryListLink
                // eslint-disable-next-line max-len
                href={`/advertisingAccounts/create?redirectOrigin=AccountManagement&selectedAccountForMarketplaceExpansion=${encodeURIComponent(globalAccountId)}`}
              />
            </ManageCountryListLinkWrapper>
          )
        }
        renderHeader={() => (
          <Flex>
            <Text size="medium">
              {bundle.getMessage('country-selector-header')}
            </Text>
            <HelpTip>
              <Text>
                {bundle.getMessage(
                  'country-selector-help-tip-content-part-one',
                )}
              </Text>
              <br />
              <Text>
                {bundle.getMessage(
                  'country-selector-help-tip-content-part-two',
                )}
              </Text>
            </HelpTip>
          </Flex>
        )}
        applyButtonText={bundle.getMessage(
          'country-selector-apply-button-label',
        )}
      />
    </ThemeProvider>
  );
};

export default MarketplaceSwitcher;
